/**
 * 점검결과 상수 정의
 * 스키마 저장 및 혼용 방지를 위해 전역에서 관리
 */

/**
 * 점검결과 값
 * - OK: 양호 (○)
 * - FAIL: 불량 (×)
 * - NA: 해당없음 (/)
 */
export const CHECK_RESULTS = {
  OK: "○",
  FAIL: "×",
  NA: "/",
} as const;

/**
 * 점검결과 값 타입
 */
export type CheckResultValue = (typeof CHECK_RESULTS)[keyof typeof CHECK_RESULTS];

/**
 * 점검결과 한글 라벨
 */
export const CHECK_RESULT_LABELS: Record<CheckResultValue, string> = {
  [CHECK_RESULTS.OK]: "양호",
  [CHECK_RESULTS.FAIL]: "불량",
  [CHECK_RESULTS.NA]: "해당없음",
};

/**
 * 점검결과 UI 색상 (Tailwind CSS)
 * 클라이언트 컴포넌트에서 사용
 */
export const CHECK_RESULT_COLORS: Record<CheckResultValue, string> = {
  [CHECK_RESULTS.OK]: "bg-green-100 text-green-800 hover:bg-green-200",
  [CHECK_RESULTS.FAIL]: "bg-red-100 text-red-800 hover:bg-red-200",
  [CHECK_RESULTS.NA]: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
};

/**
 * 점검결과 옵션 배열 (UI 렌더링용)
 */
export const CHECK_RESULT_OPTIONS = Object.entries(CHECK_RESULTS).map(([key, value]) => ({
  key,
  value,
  label: CHECK_RESULT_LABELS[value],
  color: CHECK_RESULT_COLORS[value],
}));

// ============================================
// 점검 항목 그룹 (유지관리용)
// ============================================

/**
 * 점검 항목 그룹 enum
 * - "": 빈 값 (점검항목용)
 * - "외관": 외관 점검
 * - "기능": 기능 점검
 * - "안전": 안전 점검
 */
export const CHECK_ITEM_GROUPS = ["", "외관", "기능", "안전"] as const;

/**
 * 점검 항목 그룹 타입
 */
export type CheckItemGroup = (typeof CHECK_ITEM_GROUPS)[number];

/**
 * 유지관리용 그룹 (빈 값 제외)
 */
export const MAINTENANCE_GROUPS = ["외관", "기능", "안전"] as const;

/**
 * 유지관리 그룹 UI 색상 (Tailwind CSS)
 */
export const MAINTENANCE_GROUP_COLORS = {
  외관: {
    color: "bg-blue-500",
    textColor: "text-blue-600",
    bgLight: "bg-blue-50",
  },
  기능: {
    color: "bg-green-500",
    textColor: "text-green-600",
    bgLight: "bg-green-50",
  },
  안전: {
    color: "bg-red-500",
    textColor: "text-red-600",
    bgLight: "bg-red-50",
  },
} as const;

/**
 * 유지관리 그룹 옵션 배열 (UI 렌더링용)
 */
export const MAINTENANCE_GROUP_OPTIONS = MAINTENANCE_GROUPS.map((value) => ({
  value,
  label: value,
  ...MAINTENANCE_GROUP_COLORS[value],
}));
